import React, { useState, useEffect } from 'react';
import {
  IndianRupee,
  Send,
  ShoppingBag,
  AlertCircle,
  HandCoins,
  Wallet,
} from 'lucide-react';
import styles from './DailyTransactionForm.module.css';

import {
  FETCH_STORES_URL,
  FETCH_STORE_USER_URL,
  CREATE_TRANSACTION_URL,
  FETCH_CASH_BALANCE_URL,
} from '../constant/apiConstants';
import axios from 'axios';

const DailyTransactionForm = () => {
  const [formData, setFormData] = useState({
    description: '',
    transferToStore: '',
    storeUser: '',
    paymentMode: 'CASH',
    transactionTypeCode: 'dr',
    transactionType: 'EXPENSE',
    amount: '',
  });

  const [stores, setStores] = useState([]);
  const [storeUsers, setStoreUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const [balances, setBalances] = useState({
    CASH_BALANCE: 0,
    UPI_BALANCE: 0,
    CARD_BALANCE: 0,
    OPEN_CASH: 0,
    OPEN_CARD: 0,
    OPEN_UPI: 0,
    TODAY_CASH_BALANCE: 0,
    TODAY_UPI_BALANCE: 0,
    TODAY_CARD_BALANCE: 0,
  });

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');
    return {
      user: user ? JSON.parse(user) : null,
      token: token || null,
    };
  };

  // Fetch cash balance
  const fetchCashBalance = async () => {
    try {
      const { user, token } = getUserData();
      if (!user || !user.storeId) return;

      const requestData = { user, token };
      const response = await axios.post(FETCH_CASH_BALANCE_URL, requestData);

      if (response.data.status === 'success') {
        const {
          cash_BALANCE,
          card_BALANCE,
          upi_BALANCE,
          opening_CASH_BALANCE,
          opening_CARD_BALANCE,
          opening_UPI_BALANCE,
          today_CASH_BALANCE,
          today_UPI_BALANCE,
          today_CARD_BALANCE,
        } = response.data.payload;
        setBalances({
          CASH_BALANCE: cash_BALANCE,
          CARD_BALANCE: card_BALANCE,
          UPI_BALANCE: upi_BALANCE,
          OPEN_CASH: opening_CASH_BALANCE,
          OPEN_CARD: opening_CARD_BALANCE,
          OPEN_UPI: opening_UPI_BALANCE,
          TODAY_CASH_BALANCE: today_CASH_BALANCE,
          TODAY_UPI_BALANCE: today_UPI_BALANCE,
          TODAY_CARD_BALANCE: today_CARD_BALANCE,
        });
      }
    } catch (error) {
      console.error('Error fetching cash balance:', error);
    }
  };

  useEffect(() => {
    fetchCashBalance();
  }, []);

  useEffect(() => {
    if (formData.transactionType === 'INCOME') {
      setFormData((prev) => ({ ...prev, transactionTypeCode: 'cr' }));
    } else if (formData.transactionType === 'EXPENSE') {
      setFormData((prev) => ({ ...prev, transactionTypeCode: 'dr' }));
    }
  }, [formData.transactionType]);

  const fetchStores = async () => {
    try {
      const { user } = getUserData();
      const response = await axios.post(`${FETCH_STORES_URL}`);
      if (response.status === 200) {
        const filteredStores = response.data.filter((store) => store.storeId !== user.storeId);
        setStores(filteredStores);
      } else {
        alert('No store found');
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  // Reset transaction type code when transaction type changes
  useEffect(() => {
    if (formData.transactionType === 'TRANSFER') {
      setFormData((prev) => ({ ...prev, transactionTypeCode: '' }));
    } else if (formData.transactionType === 'EXPENSE') {
      setFormData((prev) => ({ ...prev, transactionTypeCode: 'dr' }));
    } else if (formData.transactionType === 'INCOME') {
      setFormData((prev) => ({ ...prev, transactionTypeCode: 'cr' }));
    } else if (!formData.transactionTypeCode) {
      setFormData((prev) => ({ ...prev, transactionTypeCode: 'dr' }));
    }
  }, [formData.transactionType]);

  const fetchStoreUsers = async (storeId) => {
    try {
      const response = await axios.get(`${FETCH_STORE_USER_URL}?storeId=${storeId}`);
      if (response.data.status === 'success') {
        const users = response.data.payload;
        const defaultUser = users.find((user) => user.userType === 'default');
        setStoreUsers(users);
        setFormData((prev) => ({
          ...prev,
          storeUser: defaultUser ? defaultUser.userId : '',
        }));
      } else {
        setStoreUsers([]);
        setFormData((prev) => ({ ...prev, storeUser: '' }));
      }
    } catch (error) {
      console.error('Error fetching store users:', error);
      setStoreUsers([]);
      setFormData((prev) => ({ ...prev, storeUser: '' }));
    }
  };

  useEffect(() => {
    if (formData.transferToStore) {
      fetchStoreUsers(formData.transferToStore);
    } else {
      setStoreUsers([]);
    }
  }, [formData.transferToStore]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (success) {
      setSuccess(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (formData.transactionType === 'TRANSFER') {
      if (!formData.transferToStore) newErrors.transferToStore = 'Please select a store';
      if (!formData.storeUser) newErrors.storeUser = 'Please select a user';
    }
    if (formData.transactionType === 'EXPENSE' && !formData.transactionTypeCode) {
      newErrors.transactionTypeCode = 'Please select credit or debit';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { user, token } = getUserData();

      const requestData = {
        user,
        token,
        transactionDailyCashModel: {
          ...formData,
          amount: parseInt(formData.amount, 10),
        },
      };

      if (formData.transactionType === 'TRANSFER') {
        delete requestData.transactionDailyCashModel.transactionTypeCode;
      }

      const response = await axios.post(CREATE_TRANSACTION_URL, requestData);

      if (response.status === 200) {
        setSuccess(true);
        setFormData({
          description: '',
          transferToStore: '',
          storeUser: '',
          paymentMode: 'CASH',
          transactionTypeCode: 'dr',
          transactionType: 'EXPENSE',
          amount: '',
        });
      } else {
        throw new Error('Failed to create transaction');
      }
    } catch (err) {
      console.error('Transaction creation error:', err);
      setErrors({ form: 'Failed to create transaction' });
    } finally {
      setLoading(false);
      fetchCashBalance();
    }
  };

  const showTransferFields = formData.transactionType === 'TRANSFER';
  const isDebit = formData.transactionTypeCode === 'dr';

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h1 className={styles.title}>Create Daily Transaction</h1>
        <div className={styles.divider}></div>

        {success && <div className={styles.successMessage}>Transaction created successfully!</div>}

        {errors.form && (
          <div className={styles.errorMessage}>
            <AlertCircle size={16} />
            <span>{errors.form}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Transaction Type</label>
            <div className={styles.typeButtons}>
              <button
                type="button"
                className={`${styles.typeButton} ${styles.expenseTransferButton} ${
                  formData.transactionType === 'EXPENSE' ? styles.active : ''
                }`}
                onClick={() => handleInputChange({ target: { name: 'transactionType', value: 'EXPENSE' } })}
              >
                <ShoppingBag size={18} className={styles.icon} />
                <span>Expense</span>
              </button>

              <button
                type="button"
                className={`${styles.typeButton} ${styles.expenseTransferButton} ${
                  formData.transactionType === 'INCOME' ? styles.active : ''
                }`}
                onClick={() => handleInputChange({ target: { name: 'transactionType', value: 'INCOME' } })}
              >
                <HandCoins size={18} className={styles.icon} />
                <span>Income</span>
              </button>

              <button
                type="button"
                className={`${styles.typeButton} ${styles.expenseTransferButton} ${
                  formData.transactionType === 'TRANSFER' ? styles.active : ''
                }`}
                onClick={() => handleInputChange({ target: { name: 'transactionType', value: 'TRANSFER' } })}
              >
                <Send size={18} className={styles.icon} />
                <span>Transfer</span>
              </button>
            </div>
          </div>

          <div className={styles.section}>
            <label className={styles.sectionLabel}>Description</label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.description ? styles.error : ''}`}
              placeholder="Enter transaction description"
            />
            {errors.description && (
              <div className={styles.errorMessage}>
                <AlertCircle size={14} />
                <span>{errors.description}</span>
              </div>
            )}
          </div>

          {showTransferFields && (
            <div className={styles.transferSection}>
              <div className={styles.blueLine}></div>
              <div className={styles.row}>
                <div className={styles.column}>
                  <label className={styles.sectionLabel}>Transfer To Store</label>
                  <div className={styles.selectWrapper}>
                    <select
                      name="transferToStore"
                      value={formData.transferToStore}
                      onChange={(e) => {
                        handleInputChange(e);
                        setFormData((prev) => ({ ...prev, storeUser: '' }));
                      }}
                      className={`${styles.select} ${errors.transferToStore ? styles.error : ''}`}
                    >
                      <option value="">Select Store</option>
                      {stores.map((store) => (
                        <option key={store.storeId} value={store.storeId}>
                          {store.storeName} - {store.address}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.transferToStore && (
                    <div className={styles.errorMessage}>
                      <AlertCircle size={14} />
                      <span>{errors.transferToStore}</span>
                    </div>
                  )}
                </div>

                <div className={styles.column}>
                  <label className={styles.sectionLabel}>Store User</label>
                  <div className={styles.selectWrapper}>
                    <select
                      name="storeUser"
                      value={formData.storeUser}
                      onChange={handleInputChange}
                      className={`${styles.select} ${errors.storeUser ? styles.error : ''}`}
                      disabled={!formData.transferToStore}
                    >
                      <option value="">Select User</option>
                      {storeUsers.map((user) => (
                        <option key={user.userId} value={user.userId}>
                          {user.sname}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.storeUser && (
                    <div className={styles.errorMessage}>
                      <AlertCircle size={14} />
                      <span>{errors.storeUser}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {formData.transactionType === 'EXPENSE' && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}>Transaction Type</label>
              <div className={styles.typeButtons}>
                <button
                  type="button"
                  className={`${styles.typeButton} ${styles.debitButton} ${isDebit ? styles.active : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'transactionTypeCode', value: 'dr' } })}
                >
                  <span>Debit</span>
                </button>
              </div>
            </div>
          )}

          {formData.transactionType === 'INCOME' && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}>Transaction Type</label>
              <div className={styles.typeButtons}>
                <button
                  type="button"
                  className={`${styles.typeButton} ${styles.creditButton} ${!isDebit ? styles.active : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'transactionTypeCode', value: 'cr' } })}
                >
                  <span>Credit</span>
                </button>
              </div>
            </div>
          )}

          <div className={styles.section}>
            <label className={styles.sectionLabel}>Payment Mode</label>
            <div className={styles.paymentOptions}>
              <button
                type="button"
                className={`${styles.paymentButton} ${formData.paymentMode === 'CASH' ? styles.active : ''}`}
                onClick={() => handleInputChange({ target: { name: 'paymentMode', value: 'CASH' } })}
              >
                <IndianRupee size={18} className={styles.icon} />
                <span>Cash</span>
              </button>
            </div>
          </div>

          <div className={styles.section}>
            <label className={styles.sectionLabel}>Amount</label>
            <div className={styles.amountWrapper}>
              <span className={styles.currencySymbol}>₹</span>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                className={`${styles.input} ${styles.amountInput} ${errors.amount ? styles.error : ''}`}
                placeholder="0.00"
              />
            </div>
            {errors.amount && (
              <div className={styles.errorMessage}>
                <AlertCircle size={14} />
                <span>{errors.amount}</span>
              </div>
            )}
          </div>

          <button type="submit" className={styles.submitButton} disabled={loading}>
            {loading ? 'Processing...' : 'Create Transaction'}
          </button>
        </form>
      </div>

      {/* Balance panel */}
      <div className={styles.balanceSection}>
        <div className={styles.balanceHeader}>
          <Wallet size={18} className={styles.balanceIcon} />
          <h2 className={styles.balanceTitle}>Account Balances</h2>
        </div>

        <div className={styles.balanceCards}>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardHeader}>
              <IndianRupee size={16} className={styles.balanceCardIcon} />
              <span className={styles.balanceCardLabel}>Cash</span>
            </div>
            <div className={styles.balanceDetails}>
              <div className={styles.balanceRow}>
                <span className={styles.balanceLabel}>Opening</span>
                <span className={styles.balanceValue}>₹{balances.OPEN_CASH}</span>
              </div>
              <div className={styles.balanceRow}>
                <span className={styles.balanceLabel}>Current</span>
                <span className={`${styles.balanceValue} ${styles.currentBalance}`}>₹{balances.CASH_BALANCE}</span>
              </div>
              <div className={styles.balanceRow}>
                <span className={styles.balanceLabel}>Today</span>
                <span className={`${styles.balanceValue} ${styles.currentBalance}`}>
                  ₹{balances.TODAY_CASH_BALANCE}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyTransactionForm;